// @flow

/**
 * Value formatters for data display components.
 * Maps valueType strings to formatted display values.
 *
 * Where available, uses @auravisionlabs/aura-pkg-ui-formatter for consistency
 * with existing report widgets. Falls back to built-in formatting.
 */

let uiFormatter = null;
try {
  // $FlowFixMe - optional dependency
  uiFormatter = require('@auravisionlabs/aura-pkg-ui-formatter');
} catch (e) {
  // Formatter package not available - use built-in fallbacks
}

/**
 * Format a value based on its valueType.
 * Handles null/undefined as dash.
 */
export function formatValue(value: any, valueType: string): string {
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return '-';
  }

  // Currency types: "currency-GBP", "currency-USD", etc.
  if (typeof valueType === 'string' && valueType.startsWith('currency-')) {
    const currencyCode = valueType.slice(9);
    if (uiFormatter && uiFormatter.prettyCurrency) {
      return uiFormatter.prettyCurrency(Number(value), currencyCode);
    }
    return formatCurrencyFallback(Number(value), currencyCode);
  }

  switch (valueType) {
    case 'number':
    case 'peopleCount':
      if (uiFormatter && uiFormatter.prettyLongNumber) {
        return uiFormatter.prettyLongNumber(Number(value));
      }
      return formatNumberFallback(Number(value));

    case 'percentage':
      if (uiFormatter && uiFormatter.prettyPercentage) {
        return uiFormatter.prettyPercentage(Number(value));
      }
      return `${Number(value).toFixed(1)}%`;

    case 'seconds':
      if (uiFormatter && uiFormatter.prettyTime) {
        return uiFormatter.prettyTime(Number(value));
      }
      return formatTimeFallback(Number(value));

    case 'rate':
      if (uiFormatter && uiFormatter.prettyRate) {
        return uiFormatter.prettyRate(Number(value));
      }
      return `${Number(value).toFixed(2)}X`;

    case 'date':
      return formatDate(value);

    case 'time':
      return formatTime(value);

    case 'string':
    default:
      return String(value);
  }
}

// ── Built-in fallback formatters ────────────────────────────────────────

function formatNumberFallback(num: number): string {
  if (Math.abs(num) >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(num) >= 1000) {
    return `${(num / 1000).toFixed(num >= 10000 ? 1 : 2)}K`;
  }
  return num.toLocaleString();
}

function formatTimeFallback(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatCurrencyFallback(num: number, code: string): string {
  try {
    const symbol = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).formatToParts(0).find(p => p.type === 'currency')?.value || code;
    return `${symbol}${formatNumberFallback(num)}`;
  } catch (e) {
    return `${code} ${formatNumberFallback(num)}`;
  }
}

function formatDate(value: any): string {
  if (!value) return '-';
  try {
    const d = new Date(value);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) {
    return String(value);
  }
}

function formatTime(value: any): string {
  if (!value) return '-';
  if (typeof value === 'number') {
    const h = value % 12 || 12;
    return `${h}${value < 12 ? 'am' : 'pm'}`;
  }
  return String(value);
}

/**
 * Format a value for CSV export (raw, no abbreviation).
 */
export function formatValueForExport(value: any, valueType: string): string {
  if (value === null || value === undefined) return '';

  if (typeof valueType === 'string' && valueType.startsWith('currency-')) {
    return String(value);
  }

  switch (valueType) {
    case 'percentage':
      return `${value}%`;
    case 'seconds':
      return formatTimeFallback(Number(value));
    case 'rate':
      return `${value}X`;
    default:
      return String(value);
  }
}
